<template>
  <div class="p-6">
    <div v-if="section?.related_policies && section.related_policies.length > 0">
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
          <i class="pi pi-shield" style="color: var(--primary-gold);"></i>
          <span style="color: var(--text-primary);">Related Policies</span>
        </h3>
        <p class="text-sm" style="color: var(--text-secondary);">
          These policies are related to this framework section and provide implementation guidance.
        </p>
      </div>

      <div class="space-y-3">
        <div
          v-for="policyId in section.related_policies"
          :key="policyId"
          class="policy-card p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md"
          style="background: var(--background-primary); border-color: var(--border-light);"
          @click="navigateToPolicy(policyId)"
        >
          <div class="flex items-start gap-3">
            <i class="pi pi-shield text-xl" style="color: var(--primary-gold);"></i>
            <div class="flex-1">
              <h4 class="font-semibold mb-1" style="color: var(--text-primary);">
                Policy {{ policyId }}
              </h4>
              <p class="text-sm" style="color: var(--text-secondary);">
                {{ getPolicyTitle(policyId) }}
              </p>
            </div>
            <i class="pi pi-arrow-right" style="color: var(--text-muted);"></i>
          </div>
        </div>
      </div>

      <div v-if="section.related_policies.includes('all')" class="mt-6 p-4 rounded-lg" style="background: var(--background-secondary);">
        <div class="flex items-start gap-3">
          <i class="pi pi-info-circle text-xl" style="color: var(--primary-gold);"></i>
          <div>
            <p class="font-semibold mb-1" style="color: var(--text-primary);">All Policies</p>
            <p class="text-sm" style="color: var(--text-secondary);">
              This framework section relates to all policies in the Policy Domain.
              Navigate to the Policy Domain to explore individual policies.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <i class="pi pi-shield text-4xl mb-4" style="color: var(--text-muted);"></i>
      <p style="color: var(--text-secondary);">No related policies for this section.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Section } from '@/types/ashmatics';
import { useExplorerStore } from '@/stores/explorerStore';

const props = defineProps<{
  section: Section | null;
}>();

const explorerStore = useExplorerStore();

// Titles come from the live policy-domain data (loaded by the store's
// initialize()), so they track the current policy numbering rather than a
// hand-maintained map that can drift.
const getPolicyTitle = (policyId: string): string => {
  return explorerStore.findSectionInDomain('policy', policyId)?.title || 'View Policy Details';
};

// Open the referenced policy in the peek modal (no-op for the 'all' pseudo-id,
// which the info card below already explains).
const navigateToPolicy = (policyId: string) => {
  explorerStore.openRelatedArtifact('policy', policyId);
};
</script>

<style scoped>
.policy-card:hover {
  border-color: var(--primary-gold);
}
</style>
